// Report-experiment periodic report gate — the forced checkpoint that
// blocks other UI and hints the participant toward their arm's feature
// every `reportIntervalMs` (see AppShell's gate state and interval-tick
// effect in app.jsx).
//
// Chrome mirrors HardStopSheet's forced-interaction contract
// (extensions.jsx): outside-tap and ESC are no-ops, and the only way
// through is interacting with the gate's live target elsewhere on
// screen (the Mirror button, the Risk Meter, the Hotspots toggle, or a
// highlighted message). Unlike HardStopSheet, this overlay must let
// that one target visually "poke through" while everything else dims —
// see GATE_Z (scrim) vs GATE_TARGET_Z (target/card), both exported so
// app.jsx applies the same values when elevating a target region.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { TelemetryEvent } from '../../shared/telemetry-events.js';

// Below every `.sheet`/`.sheet-overlay` (100+) so a real sheet always wins
// if one somehow opens while a gate is active (it shouldn't: opening the
// target sheet is exactly what clears the gate). Above the device notch
// (zIndex 50, app.jsx) so the dim reads as "the whole screen paused."
export const GATE_Z = 90;
export const GATE_TARGET_Z = 92;
export const GATE_CARD_Z = 93;

const UI_STATE_STORAGE_KEY = 'privmeter-state';
const REPORT_GATE_ARMS = new Set(['F1', 'F3', 'F4']);
const F4_GATE_PHASES = new Set(['enable', 'tap']);

function readPersistedReportGate(enabledFeatures, reportGateDelayMs) {
  if (!(reportGateDelayMs > 0) || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage?.getItem(UI_STATE_STORAGE_KEY);
    const state = raw ? JSON.parse(raw) : null;
    const persisted = state?.reportGate;
    const gate = persisted?.gate;
    const activeArm = enabledFeatures.find((f) => REPORT_GATE_ARMS.has(f));
    const firstGateTriggered = Boolean(
      state?.firstGateTriggered && state?.firstGateArm && state.firstGateArm === activeArm
    );
    if (!gate || !REPORT_GATE_ARMS.has(gate.arm) || !enabledFeatures.includes(gate.arm))
      return { gate: null, armedAt: null, firstGateTriggered };
    if (gate.arm === 'F4' && !F4_GATE_PHASES.has(gate.phase))
      return { gate: null, armedAt: null, firstGateTriggered };
    if (gate.arm !== 'F4' && gate.phase != null)
      return { gate: null, armedAt: null, firstGateTriggered };
    const armedAt = Number(persisted.armedAt);
    if (!Number.isFinite(armedAt)) return { gate: null, armedAt: null, firstGateTriggered };
    return { gate, armedAt, firstGateTriggered };
  } catch (error) {
    console.warn('[report-gate] failed to restore persisted gate', error);
    return null;
  }
}

function persistReportGate(gate, armedAt, firstGateTriggered, firstGateArm = null) {
  if (typeof window === 'undefined') return;
  try {
    const raw = window.localStorage?.getItem(UI_STATE_STORAGE_KEY);
    const state = raw ? JSON.parse(raw) : {};
    state.reportGate = gate ? { gate, armedAt } : null;
    if (firstGateTriggered && firstGateArm) {
      state.firstGateTriggered = true;
      state.firstGateArm = firstGateArm;
    }
    window.localStorage?.setItem(UI_STATE_STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.warn('[report-gate] failed to persist gate state', error);
  }
}

/**
 * Owns the report-arm checkpoint state machine, interval, telemetry, and
 * reload persistence. The app supplies only current feature state and the
 * actions needed to refresh analysis and record UI events.
 */
export function useReportGate({
  enabledFeatures = [],
  reportGateDelayMs: reportGateDelayMsProp,
  reportIntervalMs: reportIntervalMsProp,
  reportRefreshIntervalMs = 60_000,
  sheet = null,
  hotspotsOn = false,
  f4DetailSeen = false,
  history = [],
  analysisErrors = {},
  isPreparationReady = true,
  refreshAnalysis,
  recordUiEvent,
}) {
  const reportGateDelayMs = reportGateDelayMsProp ?? reportIntervalMsProp ?? 0;
  const persisted = useState(() => readPersistedReportGate(enabledFeatures, reportGateDelayMs))[0];
  const [gate, setGate] = useState(() => persisted?.gate ?? null);
  const [firstGateTriggered, setFirstGateTriggered] = useState(
    () => persisted?.firstGateTriggered ?? false
  );
  const firstGateTriggeredRef = useRef(persisted?.firstGateTriggered ?? false);
  firstGateTriggeredRef.current = firstGateTriggered;
  const gateRef = useRef(gate);
  gateRef.current = gate;
  const gateShownAtRef = useRef(persisted?.armedAt ?? null);
  const gateResumedRef = useRef(Boolean(persisted?.gate));
  const recordUiEventRef = useRef(recordUiEvent);
  recordUiEventRef.current = recordUiEvent;
  const f4ErrorReportedRef = useRef(false);
  const [gatePending, setGatePending] = useState(false);
  const gatePendingRef = useRef(false);
  gatePendingRef.current = gatePending;
  const isPreparationReadyRef = useRef(isPreparationReady);
  isPreparationReadyRef.current = isPreparationReady;
  const historyRef = useRef(history);
  historyRef.current = history;
  const sheetRef = useRef(sheet);
  sheetRef.current = sheet;
  const hotspotsOnRef = useRef(hotspotsOn);
  hotspotsOnRef.current = hotspotsOn;
  const f4DetailSeenRef = useRef(f4DetailSeen);
  f4DetailSeenRef.current = f4DetailSeen;
  const unmountedRef = useRef(false);
  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
    };
  }, []);

  const f1Enabled = enabledFeatures.includes('F1');
  const f3Enabled = enabledFeatures.includes('F3');
  const f4Enabled = enabledFeatures.includes('F4');
  const hasTappable = history.some(
    (message) => message.role === 'me' && message.inferences?.length
  );

  const activeArm = enabledFeatures.find((f) => REPORT_GATE_ARMS.has(f)) ?? null;

  const armGate = useCallback(
    (nextGate) => {
      const armedAt = Date.now();
      gateShownAtRef.current = armedAt;
      gateResumedRef.current = false;
      gateRef.current = nextGate;
      firstGateTriggeredRef.current = true;
      setFirstGateTriggered(true);
      setGate(nextGate);
      persistReportGate(nextGate, armedAt, true, activeArm);
    },
    [activeArm]
  );

  useEffect(() => {
    if (!gate) {
      gateShownAtRef.current = null;
      persistReportGate(null, null, firstGateTriggered, activeArm);
      return;
    }

    const armedAt = Number.isFinite(gateShownAtRef.current) ? gateShownAtRef.current : Date.now();
    gateShownAtRef.current = armedAt;
    recordUiEventRef.current?.(TelemetryEvent.REPORT_GATE_SHOWN, {
      arm: gate.arm.toLowerCase(),
      phase: gate.phase ?? null,
    });
    if (gateResumedRef.current) {
      recordUiEventRef.current?.(TelemetryEvent.REPORT_GATE_RESUMED, {
        arm: gate.arm.toLowerCase(),
        phase: gate.phase ?? null,
        remount_elapsed_ms: Math.max(0, Date.now() - armedAt),
      });
      gateResumedRef.current = false;
    }
    persistReportGate(gate, armedAt, true, activeArm);
  }, [gate, firstGateTriggered, activeArm]);

  useEffect(() => {
    if (!(reportGateDelayMs > 0)) {
      if (gateRef.current) {
        gateRef.current = null;
        gateShownAtRef.current = null;
        setGate(null);
      }
      persistReportGate(null, null);
    }
  }, [reportGateDelayMs]);

  const clearReportGate = (clearMethod, nextGate = null) => {
    gatePendingRef.current = false;
    setGatePending(false);
    const currentGate = gateRef.current;
    if (!currentGate) {
      if (nextGate) armGate(nextGate);
      return;
    }
    const shownAt = gateShownAtRef.current ?? Date.now();
    recordUiEventRef.current?.(TelemetryEvent.REPORT_GATE_CLEARED, {
      arm: currentGate.arm.toLowerCase(),
      phase: currentGate.phase ?? null,
      clear_method: clearMethod,
      dwell_ms: Math.max(0, Date.now() - shownAt),
    });
    gateShownAtRef.current = nextGate ? Date.now() : null;
    gateResumedRef.current = false;
    gateRef.current = nextGate;
    setGate(nextGate);
    persistReportGate(nextGate, gateShownAtRef.current, firstGateTriggered, activeArm);
  };
  const tryArmPendingGate = useCallback(() => {
    if (f1Enabled && sheetRef.current === 'mirror') {
      if (!firstGateTriggeredRef.current) {
        firstGateTriggeredRef.current = true;
        setFirstGateTriggered(true);
        persistReportGate(null, null, true, activeArm);
      }
      gatePendingRef.current = false;
      setGatePending(false);
      return;
    }
    if (f3Enabled && sheetRef.current === 'meter') {
      if (!firstGateTriggeredRef.current) {
        firstGateTriggeredRef.current = true;
        setFirstGateTriggered(true);
        persistReportGate(null, null, true, activeArm);
      }
      gatePendingRef.current = false;
      setGatePending(false);
      return;
    }

    if (!isPreparationReadyRef.current) return;

    const currentHasTappable = historyRef.current.some(
      (message) => message.role === 'me' && message.inferences?.length
    );

    const nextGate =
      f1Enabled && !firstGateTriggeredRef.current
        ? { arm: 'F1' }
        : f3Enabled && !firstGateTriggeredRef.current
          ? { arm: 'F3' }
          : f4Enabled && !hotspotsOnRef.current && !firstGateTriggeredRef.current
            ? { arm: 'F4', phase: 'enable' }
            : f4Enabled && !f4DetailSeenRef.current && currentHasTappable
              ? { arm: 'F4', phase: 'tap' }
              : null;

    if (!nextGate) {
      gatePendingRef.current = false;
      setGatePending(false);
      return;
    }

    const currentGate = gateRef.current;
    gatePendingRef.current = false;
    setGatePending(false);
    if (currentGate?.arm === nextGate.arm && currentGate?.phase === nextGate.phase) return;
    armGate(nextGate);
  }, [activeArm, armGate, f1Enabled, f3Enabled, f4Enabled]);

  useEffect(() => {
    if (gatePending && isPreparationReady) {
      tryArmPendingGate();
    }
  }, [gatePending, isPreparationReady, tryArmPendingGate]);

  const gateTickRunningRef = useRef(false);
  const gateTickRef = useRef(null);
  gateTickRef.current = async () => {
    // If a gate is already pending or a gate tick is currently awaiting analysis,
    // do not trigger overlapping refreshes that would invalidate in-flight requests.
    if (gatePendingRef.current || gateTickRunningRef.current) return;
    gateTickRunningRef.current = true;
    try {
      if (f1Enabled && sheetRef.current === 'mirror') {
        if (!firstGateTriggeredRef.current) {
          firstGateTriggeredRef.current = true;
          setFirstGateTriggered(true);
          persistReportGate(null, null, true, activeArm);
        }
        return;
      }
      if (f3Enabled && sheetRef.current === 'meter') {
        if (!firstGateTriggeredRef.current) {
          firstGateTriggeredRef.current = true;
          setFirstGateTriggered(true);
          persistReportGate(null, null, true, activeArm);
        }
        return;
      }

      const shouldRefreshForGate = !firstGateTriggeredRef.current;
      const wouldGate =
        (f1Enabled && !firstGateTriggeredRef.current) ||
        (f3Enabled && !firstGateTriggeredRef.current) ||
        (f4Enabled && !hotspotsOnRef.current && !firstGateTriggeredRef.current) ||
        (f4Enabled && !f4DetailSeenRef.current);

      if (!wouldGate) {
        if (shouldRefreshForGate) refreshAnalysis?.({ immediate: true });
        return;
      }

      // Gate-delay ticks prepare the first gate and check later transitions.
      // After the first trigger, reportRefreshIntervalMs owns report refreshes.
      if (!isPreparationReadyRef.current) {
        gatePendingRef.current = true;
        setGatePending(true);
        if (shouldRefreshForGate) {
          await refreshAnalysis?.({ immediate: true });
          if (unmountedRef.current) return;
          tryArmPendingGate();
        }
      } else {
        if (shouldRefreshForGate) refreshAnalysis?.({ immediate: true });
        tryArmPendingGate();
      }
    } finally {
      gateTickRunningRef.current = false;
    }
  };
  useEffect(() => {
    if (!(reportGateDelayMs > 0)) return undefined;
    const id = setInterval(() => gateTickRef.current?.(), reportGateDelayMs);
    return () => clearInterval(id);
  }, [reportGateDelayMs]);
  useEffect(() => {
    if (!firstGateTriggered || !(reportRefreshIntervalMs > 0)) return undefined;
    const id = setInterval(() => {
      refreshAnalysis?.();
    }, reportRefreshIntervalMs);
    return () => clearInterval(id);
  }, [firstGateTriggered, reportRefreshIntervalMs, refreshAnalysis]);

  useEffect(() => {
    const blockedByF4Error = f4Enabled && hotspotsOn && !f4DetailSeen && !hasTappable;
    if (!blockedByF4Error || !analysisErrors?.F4) {
      if (!analysisErrors?.F4 || !blockedByF4Error) f4ErrorReportedRef.current = false;
      return;
    }
    if (f4ErrorReportedRef.current) return;
    f4ErrorReportedRef.current = true;
    recordUiEventRef.current?.(TelemetryEvent.REPORT_GATE_UNAVAILABLE, {
      arm: 'f4',
      phase: 'tap',
      reason: 'analysis_error',
    });
  }, [analysisErrors?.F4, f4Enabled, hasTappable, hotspotsOn, f4DetailSeen]);

  const gateHeaderTarget =
    gate?.arm === 'F1'
      ? 'mirror'
      : gate?.arm === 'F4' && gate?.phase === 'enable'
        ? 'hotspots'
        : null;
  const gateChatBlocked = Boolean(gate) && !(gate?.arm === 'F4' && gate?.phase === 'tap');
  return {
    gate,
    clearReportGate,
    gateHeaderTarget,
    gateChatBlocked,
    firstGateTriggered,
  };
}

const GATE_COPY = {
  f1: {
    eyebrow: 'Privacy check-in',
    title: 'See what the chat reveals',
    body: 'Your report gathers the details this conversation may reveal about you.',
    action: 'Open your Mirror report above to continue',
  },
  f3: {
    eyebrow: 'Privacy check-in',
    title: 'Check your privacy level',
    body: 'Your meter shows how much each person has revealed in the conversation.',
    action: 'Open your Risk Meter above to continue',
  },
  f4_enable: {
    eyebrow: 'Privacy check-in',
    title: 'Make hidden clues visible',
    body: 'Hotspots surface inferred details directly in the conversation.',
    action: 'Turn on Hotspots above to continue',
  },
  f4_tap: {
    eyebrow: 'One more thing',
    title: 'Look closer at one message',
    body: 'Open a highlighted message to see the inference behind its risk level.',
    action: 'Tap the highlighted message above to continue',
  },
};

/**
 * @param {{ arm: 'F1'|'F3'|'F4', phase?: 'enable'|'tap' }|null} gate
 * @returns {string|null}
 */
function gateCopyKey(gate) {
  if (!gate) return null;
  if (gate.arm === 'F4') return gate.phase === 'tap' ? 'f4_tap' : 'f4_enable';
  if (gate.arm === 'F1') return 'f1';
  if (gate.arm === 'F3') return 'f3';
  return null;
}

// Style helper: elevate a gate's live target above the scrim (GATE_Z) so
// it stays visible + clickable while everything else dims. Spread onto
// the target's wrapper `style` — used for RiskBar and the active message
// bubble row; ChatHeader elevates itself via its own `gateTarget` prop
// since its wrapper already owns a zIndex.
export function gateTargetStyle(isTarget) {
  return isTarget ? { position: 'relative', zIndex: GATE_TARGET_Z } : undefined;
}

// Style helper: dim a non-target region while a gate is active. Pair it with
// a real `disabled` attribute on buttons and inputs because pointer-events
// alone doesn't stop keyboard/tab focus. Reserve this helper for regions that
// do not support a disabled attribute, such as the chat scroll body.
export function gateBlockedStyle(blocked) {
  return blocked
    ? { opacity: 0.4, pointerEvents: 'none', transition: 'opacity 200ms ease' }
    : undefined;
}

function GateGlyph() {
  return (
    <svg className="report-gate-glyph" viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <path
        d="M16 3.75 26 7.5v7.4c0 6.1-4.15 10.65-10 12.35C10.15 25.55 6 21 6 14.9V7.5L16 3.75Z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
      <path
        d="M10.1 15.45s2.25-3.25 5.9-3.25 5.9 3.25 5.9 3.25-2.25 3.25-5.9 3.25-5.9-3.25-5.9-3.25Z"
        stroke="currentColor"
        strokeWidth="1.55"
        strokeLinejoin="round"
      />
      <circle cx="16" cy="15.45" r="1.65" fill="currentColor" />
    </svg>
  );
}

function UpArrow() {
  return (
    <svg className="report-gate-arrow" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path d="M9 14V4M5.5 7.5 9 4l3.5 3.5" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}

// Gap between the target's edge and the card, and the margin kept clear at
// the phone frame's own top/bottom edge so the card never touches it.
const GATE_CONNECTOR_GAP_PX = 12;
const GATE_FRAME_MARGIN_PX = 12;
// Below this, the target's visible slice inside the frame is too thin to
// anchor to (e.g. almost entirely scrolled out of view) — fall back instead.
const GATE_MIN_TARGET_VISIBLE_PX = 16;
// Keeps the connector tail off the card's 22px rounded corners.
const GATE_TAIL_INSET_PX = 20;
// Triangle height. Fed to styles.css as the --gate-connector-size inline
// custom property below (.report-gate-connector--below / --above) so
// this stays the single source of truth instead of a hand-synced literal.
const GATE_CONNECTOR_SIZE_PX = 10;

/**
 * Decides where to place the coach card relative to its live target, and
 * where the connector triangle that points from the card to the target
 * should sit. All returned coordinates are relative to `containerRect`'s
 * own top/left (i.e. ready to assign to the card/connector's `top`/`left`
 * inline styles, since both are absolutely positioned within that same
 * containing block). Returns null when the target isn't usably visible
 * inside the frame, or when neither placement fits — callers should fall
 * back to the card's default bottom-pinned CSS position.
 *
 * @param {{top:number,bottom:number,left:number,right:number,height:number}} containerRect
 * @param {{top:number,bottom:number,left:number,right:number}} targetRect
 * @param {{left:number,right:number,height:number}} cardRect
 * @returns {{placement:'above'|'below',cardTop:number,connectorTop:number,tailX:number}|null}
 */
export function computeGateLayout(containerRect, targetRect, cardRect) {
  if (!(containerRect.height > 0) || !(cardRect.height > 0)) return null;

  const visibleTop = Math.max(targetRect.top, containerRect.top);
  const visibleBottom = Math.min(targetRect.bottom, containerRect.bottom);
  if (visibleBottom - visibleTop < GATE_MIN_TARGET_VISIBLE_PX) return null;

  const targetTop = targetRect.top - containerRect.top;
  const targetBottom = targetRect.bottom - containerRect.top;
  const belowTop = targetBottom + GATE_CONNECTOR_GAP_PX;
  const aboveTop = targetTop - GATE_CONNECTOR_GAP_PX - cardRect.height;

  let placement;
  let cardTop;
  if (belowTop + cardRect.height <= containerRect.height - GATE_FRAME_MARGIN_PX) {
    placement = 'below';
    cardTop = belowTop;
  } else if (aboveTop >= GATE_FRAME_MARGIN_PX) {
    placement = 'above';
    cardTop = aboveTop;
  } else {
    return null;
  }

  const cardLeft = cardRect.left - containerRect.left;
  const cardRight = cardRect.right - containerRect.left;
  const targetCenterX = (targetRect.left + targetRect.right) / 2 - containerRect.left;
  const tailX = Math.min(
    Math.max(targetCenterX, cardLeft + GATE_TAIL_INSET_PX),
    cardRight - GATE_TAIL_INSET_PX
  );
  const connectorTop =
    placement === 'below' ? cardTop - GATE_CONNECTOR_SIZE_PX : cardTop + cardRect.height;

  return { placement, cardTop, connectorTop, tailX };
}

// DOM-facing wrapper around computeGateLayout: finds the phone frame
// (nearest `.device-wrap` ancestor of the card) and the live target (the
// shared `report-gate-target` className every target-marking call site
// already applies — see gateTargetStyle above). When a gate phase allows
// more than one simultaneous target (F4's 'tap' phase highlights every
// tappable message), this anchors to the first one in DOM order.
export function measureGateLayout(cardEl) {
  if (!cardEl) return null;
  const containerEl = cardEl.closest('.device-wrap');
  const targetEl = document.querySelector('.report-gate-target');
  if (!containerEl || !targetEl) return null;
  return computeGateLayout(
    containerEl.getBoundingClientRect(),
    targetEl.getBoundingClientRect(),
    cardEl.getBoundingClientRect()
  );
}

// rAF-coalesced scheduling for scroll/resize-driven remeasures, with a
// setTimeout fallback for environments without requestAnimationFrame
// (jsdom, as used by this file's test suite).
function scheduleFrame(callback) {
  if (typeof window.requestAnimationFrame === 'function') {
    const id = window.requestAnimationFrame(callback);
    return () => window.cancelAnimationFrame(id);
  }
  const id = window.setTimeout(callback, 16);
  return () => window.clearTimeout(id);
}

// Blocking scrim + non-dismissable coach card. Renders nothing when no
// gate is active.
export function ReportGate({ gate }) {
  const cardRef = useRef(null);
  const [layout, setLayout] = useState(null);

  useEffect(() => {
    if (!gate) return undefined;
    cardRef.current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [gate]);

  // Anchor the card next to its live target (a header button, RiskBar, or
  // highlighted message — all share the report-gate-target className,
  // see gateTargetStyle) instead of leaving it pinned to the frame's
  // bottom edge. Re-measures on resize and on chat-scroll since the F4
  // 'tap' phase's target lives inside the scrollable message list and can
  // move while the gate is up. useLayoutEffect (not useEffect) so the
  // first measurement commits before paint — no bottom-pinned flash on a
  // fresh gate.
  useLayoutEffect(() => {
    if (!gate) {
      setLayout(null);
      return undefined;
    }

    const measureNow = () => setLayout(measureGateLayout(cardRef.current));
    let cancelScheduled = null;
    const scheduleMeasure = () => {
      if (cancelScheduled) return;
      cancelScheduled = scheduleFrame(() => {
        cancelScheduled = null;
        measureNow();
      });
    };

    measureNow();

    const chatScrollEl = document.querySelector('[data-chat-scroll]');
    window.addEventListener('resize', scheduleMeasure);
    chatScrollEl?.addEventListener('scroll', scheduleMeasure, { passive: true });

    return () => {
      cancelScheduled?.();
      window.removeEventListener('resize', scheduleMeasure);
      chatScrollEl?.removeEventListener('scroll', scheduleMeasure);
    };
  }, [gate]);

  const copy = GATE_COPY[gateCopyKey(gate)];
  if (!copy) return null;

  return (
    <>
      <div
        className="report-gate-scrim"
        role="presentation"
        // Non-dismissable: the scrim itself is not a button the
        // participant can tap through, matching HardStopSheet's
        // outside-tap-is-a-no-op contract (extensions.jsx).
        onClick={(e) => {
          e.stopPropagation();
        }}
      />
      {layout && (
        // .report-gate-card has overflow: hidden (clips its own
        // decorative ::after glow), so this can't be a card pseudo-element
        // — it has to be an independent sibling that points at it instead.
        <div
          aria-hidden="true"
          className={`report-gate-connector report-gate-connector--${layout.placement}`}
          style={{
            top: layout.connectorTop,
            left: layout.tailX,
            zIndex: GATE_TARGET_Z,
            '--gate-connector-size': `${GATE_CONNECTOR_SIZE_PX}px`,
          }}
        />
      )}
      <div
        ref={cardRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="report-gate-title"
        aria-describedby="report-gate-body"
        tabIndex={-1}
        className="report-gate-card"
        style={layout ? { top: layout.cardTop, bottom: 'auto' } : undefined}
      >
        <div className="report-gate-heading">
          <div className="report-gate-mark">
            <GateGlyph />
          </div>
          <div className="report-gate-heading-copy">
            <div className="eyebrow report-gate-eyebrow">{copy.eyebrow}</div>
            <h2 id="report-gate-title" className="report-gate-title">
              {copy.title}
            </h2>
          </div>
        </div>
        <div id="report-gate-body" className="report-gate-body">
          <span>{copy.body}</span>
          <div className="report-gate-action">
            <span className="report-gate-action-dot" aria-hidden="true" />
            <span>{copy.action}</span>
            <UpArrow />
          </div>
        </div>
      </div>
    </>
  );
}
